import { LogIn, User, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useLoginRedirect } from '@/hooks/use-login-redirect';
import { useSession } from '@/hooks/use-session';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export function UserDropdownMenu({
  // `stable` forces the desktop layout (auth pills shown, mobile icon hidden)
  // regardless of viewport. Both the header fit-probe and the real desktop
  // topbar pass it so neither changes at the `lg`/`xl` breakpoints — see the
  // note in header-topbar.tsx. The pills are content-width (not fixed) so the
  // longer German labels can't overflow them.
  stable = false,
  // `compactAuth` (with `stable`) collapses the Register/Login pills into the
  // single profile icon — the header's narrow-but-not-mobile state.
  compactAuth = false,
}: { stable?: boolean; compactAuth?: boolean } = {}) {
  const { data: session } = useSession();
  const loginRedirect = useLoginRedirect();
  const { t } = useTranslation();
  // Login button is the default (SSR + loading + unauthenticated).
  // Only replaced by avatar after a successful auth response.
  if (!session) {
    const goRegister = () => {
      window.location.href = `/auth/login?screen_hint=signup&returnTo=${encodeURIComponent(window.location.pathname)}`;
    };

    return (
      <>
        {/* Desktop: side-by-side Register + Login pills. Content-width with a
            min-w floor and whitespace-nowrap — fixed widths clipped the longer
            German labels ("Registrieren"/"Anmelden"). Hidden in compactAuth. */}
        <div
          className={cn(
            'items-center gap-1.5',
            stable ? (compactAuth ? 'hidden' : 'flex') : 'hidden lg:flex',
          )}
        >
          <Button
            variant="outline"
            size="sm"
            className="inline-flex justify-center items-center gap-1 lg:gap-1.5 text-[11px] lg:text-base font-medium px-3 lg:px-4 h-7 lg:h-9 leading-none rounded-full whitespace-nowrap min-w-[92px]"
            onClick={goRegister}
          >
            <UserPlus className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            {t('common.register', 'Register')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            // border-transparent matches the outline (Register) button's 1px
            // border box so both have the same content area / padding.
            className="inline-flex justify-center items-center gap-1 lg:gap-1.5 text-[11px] lg:text-base font-medium px-3 lg:px-4 h-7 lg:h-9 leading-none rounded-full border border-transparent whitespace-nowrap min-w-[92px]"
            onClick={() => loginRedirect()}
          >
            <LogIn className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            {t('common.login', 'Login')}
          </Button>
        </div>

        {/* Single person icon that opens a dropdown with both actions. Shown
            below lg in the responsive (mobile-header) layout, and in the
            desktop row's compactAuth state. */}
        <div
          className={stable ? (compactAuth ? 'flex' : 'hidden') : 'flex lg:hidden'}
        >
          {/* modal={false} disables Radix's scroll lock, which otherwise adds
              scrollbar-compensation padding to <body> and shifts the whole page
              right when the menu opens. */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="dim"
                mode="icon"
                aria-label={t('common.login', 'Login')}
                className="min-h-[44px] min-w-[44px]"
              >
                <User />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-48"
              side="bottom"
              align="end"
              sideOffset={8}
              collisionPadding={12}
            >
              <DropdownMenuItem
                onSelect={() => loginRedirect()}
                className="flex items-center gap-2"
              >
                <LogIn />
                {t('common.login', 'Login')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={goRegister}
                className="flex items-center gap-2"
              >
                <UserPlus />
                {t('common.register', 'Register')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </>
    );
  }

  return null;
}
